import React from 'react';
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import MuiDialogTitle from '@mui/material/DialogTitle';
import MuiDialogContent from '@mui/material/DialogContent';
import MuiDialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import UIKitIcon from './Icon';
import { colors } from '../theme';

const StyledDialogTitle = styled(MuiDialogTitle)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}));

const TitleText = styled(Typography)({
  color: colors.neutral[700],
  fontWeight: 600,
  fontSize: '22px',
}) as typeof Typography;

export interface UIKitModalTitleProps {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const UIKitDialogTitle = ({ children, onClose, ...other }: UIKitModalTitleProps) => (
  <StyledDialogTitle {...other}>
    {/* MUI v5 renderiza MuiDialogTitle siempre como <h2> (v4 lo evitaba con
      disableTypography, prop eliminada en v5) - sin `component="span"` acá
      quedaba un <h6> anidado dentro del <h2>, HTML inválido. El estilo
      visual (variant="h6") se mantiene igual, sólo cambia el tag. */}
    <TitleText variant="h6" component="span">
      {children}
    </TitleText>
    {onClose ? (
      <CloseButton aria-label="close" onClick={onClose} size="large">
        <UIKitIcon name="close" />
      </CloseButton>
    ) : null}
  </StyledDialogTitle>
);

const UIKitModalContent = styled(MuiDialogContent)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const UIKitModalActions = styled(MuiDialogActions)(({ theme }) => ({
  margin: 0,
  padding: theme.spacing(1),
}));

interface UIKitModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  actions: React.ReactNode;
  size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: '5px !important',
    padding: '10px !important',
  },
});

const UIKitModal = ({ open, onClose, title, children, actions, size = 'md' }: UIKitModalProps) => (
  <StyledDialog
    onClose={onClose}
    aria-labelledby="customized-dialog-title"
    open={open}
    maxWidth={size}
    fullWidth
  >
    <UIKitDialogTitle id="customized-dialog-title" onClose={onClose}>
      {title}
    </UIKitDialogTitle>
    <UIKitModalContent>{children}</UIKitModalContent>
    <UIKitModalActions>{actions}</UIKitModalActions>
  </StyledDialog>
);

export default UIKitModal;
